/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useRoute } from '@react-navigation/native';
import {
  StorefrontHubScreen,
  DeliverySettingsScreen,
} from '../../../settings/public-api';
import { StoreReviewsScreen } from '../../../store-reviews/public-api';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { getManifestItemsForSurface } from '../navigationManifest';
import { useNavVisibilityContext } from '../useNavVisibilityContext';
import { HubSegmentChips, type HubSegmentChip } from './HubSegmentChips';

type StorefrontSegment = 'store' | 'delivery' | 'reviews';

/** Route params accepted by StorefrontHub for deep-linking to a segment. */
export type StorefrontHubRouteParams = {
  initialSegment?: StorefrontSegment;
};

/**
 * «Tienda» siempre visible (la puerta ya filtra por flag public_store_features
 * + módulo BranchSettings + storePublished a nivel zona). Delivery y Reseñas
 * heredan el gate de sus ítems (stackOnly): mismo flag + módulo.
 */
const SEGMENT_DEFS: Array<
  HubSegmentChip<StorefrontSegment> & { childRoute: string | null }
> = [
  { key: 'store', label: 'Tienda', testID: 'storefront-zone.segment.store', childRoute: null },
  { key: 'delivery', label: 'Delivery y zonas', testID: 'storefront-zone.segment.delivery', childRoute: 'DeliverySettings' },
  { key: 'reviews', label: 'Reseñas', testID: 'storefront-zone.segment.reviews', childRoute: 'StoreReviews' },
];

/**
 * Zona «Tienda online» como una sola puerta: la pantalla operativa de la
 * tienda sigue siendo el segmento por defecto; Delivery y Reseñas quedan a un
 * chip sin segundo nivel de menú. Monta las pantallas existentes sin tocarlas
 * — cada una conserva su header, KPIs y acciones.
 * Con un solo segmento visible no se renderiza la fila de chips.
 */
export function StorefrontZoneScreen() {
  const route = useRoute<any>();
  const navCtx = useNavVisibilityContext();

  const visibleChips = useMemo(() => {
    const stackItems = getManifestItemsForSurface('stackOnly', navCtx);
    const visibleRoutes = new Set(stackItems.map((item) => item.route));
    return SEGMENT_DEFS.filter(
      (def) => def.childRoute === null || visibleRoutes.has(def.childRoute),
    );
  }, [navCtx]);

  const initialSegmentParam = route.params?.initialSegment as StorefrontSegment | undefined;
  const [segment, setSegment] = useState<StorefrontSegment>(
    initialSegmentParam ?? 'store',
  );

  useEffect(() => {
    const next = route.params?.initialSegment as StorefrontSegment | undefined;
    if (next) {
      setSegment(next);
    }
  }, [route.params?.initialSegment]);

  const active = visibleChips.some((chip) => chip.key === segment)
    ? segment
    : (visibleChips[0]?.key ?? 'store');

  return (
    <View style={styles.root}>
      {visibleChips.length > 1 ? (
        <HubSegmentChips segments={visibleChips} active={active} onChange={setSegment} />
      ) : null}
      <View style={styles.body}>
        {active === 'store' ? <StorefrontHubScreen /> : null}
        {active === 'delivery' ? <DeliverySettingsScreen /> : null}
        {active === 'reviews' ? <StoreReviewsScreen /> : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: ticketColors.bg,
  },
  body: { flex: 1, minHeight: 0 },
});
